import get from 'lodash/get';
import { FormScopeConfig, RelOption } from '@/interfaces';
import { $t } from '@/utils';
import { geoTypes } from '@/modules/taxonomy/config';
import TaxonomySelector from '@/modules/taxonomy/selector/Selector.vue';
import SiteSelector from '@/modules/site/Selector.vue';

const defaultLinkTarget = false;
const defaultLinkFollow = false;

/**
 * Available options for the `rel` attribute.
 * Each option is keyed so it can be easily overrided in sites.
 * Each option is defined by:
 *   - label: the label that will be shown to the user.
 *   - field: the field name that will be sent to the server.
 *   - default: optional default value.
 */
const relOptions: Record<string, RelOption> = {
  nofollow: {
    label: 'nofollow',
    field: 'nofollow',
  },
};

/**
 * The fields config of the navigation wizards.
 */
const wizards: Record<string, Record<number, FormScopeConfig>> = {
  default: {
    0: {
      fieldsConfig: [
        {
          component: 'NxFormText',
          field: 'name',
          label: 'name',
          params: {
            autofocus: true,
            required: true,
          },
        },
        {
          component: 'NxNavigationUrl',
          field: 'link',
          label: 'url',
          params: {
            required: true,
          },
        },
      ],
      scope: 'navigation',
      icon: 'list',
      title: 'navigation',
    },
  },
};
wizards['main-menu'] = {
  0: {
    fieldsConfig: [
      {
        component: 'NxFormText',
        field: 'name',
        label: 'name',
        params: {
          autofocus: true,
          required: true,
        },
      },
      {
        component: 'NxNavigationUrl',
        field: 'link',
        label: 'url',
        params: {
          required: true,
        },
      },
    ],
    scope: 'navigation',
    icon: 'list',
    title: 'navigation',
  },
  1: wizards.default[0],
};

const getWizardFieldsConfig = (type: string, depth: number = 0) => {
  const fieldsConfig = get(wizards, `${type}.${depth}`, false)
    || get(wizards, `${type}.0`, false)
    || wizards.default['0'];
  if (wizardFieldsConfigByTypeHandlers.has(type)) {
    return wizardFieldsConfigByTypeHandlers.get(type)(fieldsConfig, depth);
  }
  return fieldsConfig;
};

// Las taxonomías aceptan un componente ya que estas podrían ser personalizables por projecto
// TODO idealmente los contenidos y demas opciones también deberían serlo
type NavigationUrlTaxonomy = NavigationUrl & { component: Vue.Component, props: Record<string, any> };

type NavigationUrl = {
    text: string,
    value: string,
    icon: string,
    getNodeDescription: (node: object) => ({
        text: string,
        icon: string,
    })
}

const navigationUrlConfig: Record<string, NavigationUrl|NavigationUrlTaxonomy> = {
  manual: {
    text: $t('message.wizard.navigation.manual'),
    value: 'manual',
    icon: 'edit',
    getNodeDescription: node => ({
      text: get(node, 'link.url', ''),
      icon: 'edit',
    }),
  },
  tag: {
    text: $t('message.wizard.navigation.tag'),
    value: 'tag',
    icon: 'label',
    getNodeDescription: node => ({
      text: get(node, 'link.entity.name', ''),
      icon: 'label',
    }),
  },
  board: {
    text: $t('message.wizard.navigation.board'),
    value: 'board',
    icon: 'dashboard',
    getNodeDescription: node => ({
      text: get(node, 'link.entity.title', ''),
      icon: 'dashboard',
    }),
  },
  site: {
    text: $t('message.wizard.navigation.site'),
    value: 'site',
    icon: 'home',
    component: SiteSelector,
    props: {
      emitObject: true,
      multiple: false,
      type: 'site',
      key: 'site-selected',
      validation: 'required',
    },
    getNodeDescription: node => ({
      text: get(node, 'link.entity.name', ''),
      icon: 'home',
    }),
  },
  content: {
    text: $t('message.wizard.navigation.content'),
    value: 'content',
    icon: 'text_fields',
    getNodeDescription: node => ({
      text: get(node, 'link.entity.title', ''),
      icon: 'text_fields',
    }),
  },
  empty: {
    text: $t('message.wizard.navigation.empty'),
    value: 'empty',
    icon: 'link_off',
    getNodeDescription: node => ({
      text: $t('message.wizard.navigation.empty'),
      icon: 'link_off',
    }),
  },
  blog: {
    text: $t('message.wizard.navigation.blog'),
    value: 'blog',
    icon: 'folder',
    component: TaxonomySelector,
    props: {
      label: $t('message.wizard.navigation.chooseBlog'),
      additionalFilters: { active: 'yes' },
      type: 'blog',
      key: 'blog-selected',
      limit: 1,
      validation: 'required',
    },
    getNodeDescription: node => ({
      text: get(node, 'link.entity.name', ''),
      icon: 'folder',
    }),
  },
  author: {
    text: $t('message.wizard.navigation.author'),
    value: 'author',
    icon: 'person',
    component: TaxonomySelector,
    props: {
      label: $t('message.wizard.navigation.chooseAuthor'),
      limit: 1,
      type: 'author',
      key: 'author-selected',
      validation: 'required',
    },
    getNodeDescription: node => ({
      text: get(node, 'link.entity.name', ''),
      icon: 'person',
    }),
  },
  geo: {
    text: $t('message.wizard.navigation.geo'),
    value: 'geo',
    icon: 'my_location',
    component: TaxonomySelector,
    props: {
      allowLandingPage: false,
      type: geoTypes,
      limit: 1,
      key: 'geo-selected',
      validation: 'required',
    },
    getNodeDescription: node => ({
      text: get(node, 'link.entity.name', ''),
      icon: 'my_location',
    }),
  },
  microsite: {
    text: $t('message.wizard.navigation.microsite'),
    value: 'microsite',
    icon: 'folder',
    component: TaxonomySelector,
    props: {
      label: $t('message.wizard.navigation.chooseMicrosite'),
      limit: 1,
      additionalFilters: { active: 'yes' },
      type: 'microsite',
      key: 'microsite-selected',
      validation: 'required',
    },
    getNodeDescription: node => ({
      text: get(node, 'link.entity.name', ''),
      icon: 'folder',
    }),
  },
  section: {
    text: $t('message.wizard.navigation.section'),
    value: 'section',
    icon: 'folder',
    component: TaxonomySelector,
    props: {
      label: $t('message.wizard.navigation.chooseSection'),
      limit: 1,
      additionalFilters: { active: 'yes' },
      type: 'section',
      key: 'section-selected',
      validation: 'required',
    },
    getNodeDescription: node => ({
      text: get(node, 'link.entity.name', ''),
      icon: 'folder',
    }),
  },
};

// List of types that should be excluded in a specific site.
// const siteExcludedTypes = { bemad: ['series', 'show'] };
const siteExcludedTypes = {};
const keepOnlyNavigationEntityAttributes = ['id', 'type', 'name', 'title'];
const filterNavigationEntityAttributes = (entity) => {
  if (!entity || !entity.id) return entity;

  const filteredEntity: Record<string, any> = { id: entity.id };
  keepOnlyNavigationEntityAttributes.forEach((key) => {
    if (key in entity) filteredEntity[key] = entity[key];
  });
  return filteredEntity;
};

// Customizable wizard config by navigation type
// Use the registerCustomWizardFieldsConfigHandlerForType function to assign a site handler
const wizardFieldsConfigByTypeHandlers = new Map();
export const registerCustomWizardFieldsConfigHandlerForType = (type, handler) => {
  wizardFieldsConfigByTypeHandlers.set(type, handler);
};

export default {
  wizards,
  getWizardFieldsConfig,
  navigationUrlConfig,
  siteExcludedTypes,
  defaultLinkFollow,
  defaultLinkTarget,
  relOptions,
  keepOnlyNavigationEntityAttributes,
  filterNavigationEntityAttributes,
  registerCustomWizardFieldsConfigHandlerForType,
};
